import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  optionItem: {
    marginBottom: '.5rem',
    border: '1px solid #d0d0d0', // Light gray border for option items - more visible
    borderRadius: 4,
    color: 'rgba(0, 0, 0, 0.95)', // Darker text for better contrast
    maxWidth: 770,
    position: 'relative',
    paddingLeft: '40px', // Reduced padding to accommodate smaller icon
    paddingTop: '4px', // Reduced top padding
    paddingBottom: '4px', // Reduced bottom padding
    '& .MuiListItemText-secondary': {
      color: 'rgba(0, 0, 0, 0.8)', // Darker secondary text
      marginTop: '2px', // Reduce space between primary and secondary text
    },
    // More padding for items without descriptions to improve mobile tap targets
    '&:not(:has(.MuiListItemText-secondary))': {
      paddingTop: '12px',
      paddingBottom: '12px',
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      left: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '18px', // Reduced from 24px
      height: '18px', // Reduced from 24px
      borderRadius: '50%',
      border: '2px solid currentColor',
      boxSizing: 'border-box',
    },
    '&.Mui-selected': {
      backgroundColor: '#f0f0f0', // Gray background for non-required sections
      color: 'rgba(0, 0, 0, 0.95)', // Even darker text when selected
      fontWeight: 600,
      border: '1px solid #2196f3', // Blue border when selected
      '& .MuiListItemText-secondary': {
        color: 'rgba(0, 0, 0, 0.85)', // Even darker secondary text when selected
      },
      '&:before': {
        border: '2px solid currentColor',
      },
      '&:after': {
        content: '""',
        position: 'absolute',
        left: '14px', // Adjusted to center in the smaller circle
        top: '50%',
        transform: 'translateY(-50%)',
        width: '10px', // Reduced from 12px
        height: '10px', // Reduced from 12px
        borderRadius: '50%',
        backgroundColor: 'currentColor',
      },
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#f0f0f0', // Keep the same background on hover when selected
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  optionGroup: {
    marginBottom: '1.5rem',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: '1px solid #e0e0e0', // Light gray border for non-required options
    '& .MuiListItem-root': {
      border: '1px solid #d0d0d0', // Light gray for option items - more visible
    },
  },
  requiredOptionGroup: {
    marginBottom: '1.5rem',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: '1px solid #ebe2bb', // Yellow border for required options
    backgroundColor: '#fffdf5', // Light yellow background for required options
    '& .MuiListItem-root': {
      border: '1px solid #e8e8e8', // Light gray for option items in required sections
    },
    '& .Mui-selected': {
      backgroundColor: '#f7dc6f', // Darker yellow for selected items in required sections
      '&:hover': {
        backgroundColor: '#f7dc6f', // Keep the same background on hover when selected
      },
    },
  },
  requiredOptionGroupSelected: {
    marginBottom: '1.5rem',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: '1px solid #e0e0e0', // Same as non-required options when selected
    backgroundColor: '#ffffff', // White background when selected
    '& .MuiListItem-root': {
      border: '1px solid #d0d0d0', // Light gray for option items
    },
    '& .Mui-selected': {
      backgroundColor: '#f0f0f0', // Same gray as non-required sections
      '&:hover': {
        backgroundColor: '#f0f0f0', // Keep the same background on hover when selected
      },
    },
  },
  optionTitle: {
    marginBottom: '0.1rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  requiredBadge: {
    backgroundColor: '#8b4513',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'capitalize',
    transition: 'opacity 0.3s ease',
    textShadow: 'none',
    filter: 'none',
    '-webkit-font-smoothing': 'antialiased',
    '-moz-osx-font-smoothing': 'grayscale',
    letterSpacing: '1px',
  },
  optionSubtitle: {
    marginBottom: '.35rem',
  },
  optionList: {
    padding: 0,
  },
}));

const OptionsSection = ({ 
  title, 
  subtitle, 
  options, 
  selectedValue, 
  onOptionChange, 
  isRequired = false,
  getOptionLabel,
  getOptionDescription,
  currency
}) => {
  const classes = useStyles();
  const isOptionSelected = selectedValue;

  // Determine the appropriate class for the option group
  let optionGroupClass;
  if (isRequired) {
    optionGroupClass = isOptionSelected ? classes.requiredOptionGroupSelected : classes.requiredOptionGroup;
  } else {
    optionGroupClass = classes.optionGroup;
  }

  return (
    <Box className={optionGroupClass}>
      <Typography
        variant="h6"
        className={classes.optionTitle}
      >
        {title}
        {isRequired && !isOptionSelected && (
          <span className={classes.requiredBadge}>
            Required
          </span>
        )}
      </Typography>
      {subtitle && (
        <Typography color="textPrimary" variant="body2" className={classes.optionSubtitle}>
          {subtitle}
        </Typography>
      )}
      <List className={classes.optionList}>
        {options.map((option) => {
          const label = getOptionLabel ? getOptionLabel(option) : option.label;
          const description = getOptionDescription ? getOptionDescription(option) : null;

          return (
            <ListItem
              key={option.id}
              button
              selected={selectedValue === option.id}
              onClick={() => onOptionChange(option.id)}
              className={classes.optionItem}
              disableRipple
            >
              <ListItemText 
                primary={label}
                secondary={description}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default OptionsSection;
